import { Grid, Link, PaperProps, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import Widget from 'shared/components/Widget';
import { KFButton } from '@kf/ic2fx';
import EmployeeChip from './EmployeeChip';
import CreatePanelIcon from 'shared/icons/createPanelIcon';
import InterviewersModal from './interviewersPanelModal';
import useInterviewPanel from 'modules/hiring/hooks/useInterviewPanel';
import { IInterviewers, HiringActivity } from 'modules/hiring/models/candidatePipeline.model';
import SuccessInterviewerPanelModal from './successInterviewerPanelModal';
import {
  axiosUpdateInterview,
  axiosCreateInterviewPanel,
} from 'modules/hiring/stateManagement/api';
import { Canceler } from 'axios';
import { getDecodedB2cToken } from 'modules/auth/components/b2c/utils';
import { storagePersistedKey } from 'utilities/constants';
import { useTranslation } from 'react-i18next';

// Stages where interview panel should NOT be auto-created (HP 1.0 & 2.0)
const NO_AUTO_CREATE_STAGES: string[] = [
  HiringActivity.Sourced,
  HiringActivity.Added,
  HiringActivity.InvitedToApply,
  HiringActivity.Applied,
  HiringActivity.Screen,
  HiringActivity.InScreen,
];

export interface IInterviewersProps extends PaperProps {
  id: string;
  requisitionId: string;
  jobTitle: string;
  hiringManagerEmail?: string;
  currentStep?: string;
}

const Interviewers: React.FC<IInterviewersProps> = ({
  id,
  requisitionId,
  jobTitle,
  hiringManagerEmail,
  currentStep,
  ...props
}) => {
  const { t: translate } = useTranslation();
  let makeInterviewPanelCanceler: Canceler;
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [isPanel, setIsPanel] = useState<boolean>(false);
  const [apiLoader, setApiLoader] = useState<boolean>(false);
  const [interviewerList, setInterviewerList] = useState<IInterviewers[]>([]);
  const [loginUser, setLoginUser] = useState<any>();
  const hasAutoCreated = useRef<string | null>(null);

  const { interviewPanelDetails, loading: isLoading } =
    useInterviewPanel(requisitionId);

  const autoCreatePanel = async (email: string) => {
    setApiLoader(true);
    const payload = {
      requisitionId: requisitionId,
      organizationId: loginUser?.organization_id || '',
      interviewers: [{ interviewerName: '', interviewerEmail: email }],
    };
    const response = await axiosCreateInterviewPanel(
      payload,
      (cancel: Canceler) => (makeInterviewPanelCanceler = cancel)
    );
    if (response?.statusCode === 200) {
      setIsPanel(true);
      setInterviewerList([{ interviewerEmail: email, interviewerName: '', saved: true }]);
    }
    setApiLoader(false);
  };

  useEffect(() => {
    getCurrentUserDetails();
  }, []);
  useEffect(() => {
    const panelInterviewers = interviewPanelDetails?.interviewer;
    const hasPanel =
      Array.isArray(panelInterviewers) && panelInterviewers.length > 0;
    if (hasPanel) {
      setInterviewerList(panelInterviewers.map((e) => ({ ...e, saved: true })));
      setIsPanel(true);
    } else if (
      hiringManagerEmail &&
      !isLoading &&
      currentStep &&
      !NO_AUTO_CREATE_STAGES.includes(currentStep) &&
      hasAutoCreated.current !== requisitionId
    ) {
      // Auto-create panel for Submit, Interview, Offer stages (not early stages)
      hasAutoCreated.current = requisitionId;
      autoCreatePanel(hiringManagerEmail);
    } else {
      // Early stages or no HM email - show "Invite Interviewers" button
      setInterviewerList([]);
      setIsPanel(false);
    }
  }, [interviewPanelDetails, requisitionId, isLoading, hiringManagerEmail, currentStep]);

  const onSubmitSuccess = async (interviewsPayload: IInterviewers[]) => {
    setOpenModal(false);
    setOpenSuccessModal(true);
    setApiLoader(true);

    const payload = {
      requisitionId: requisitionId,
      organizationId: loginUser?.organization_id || '',
      interviewers: interviewsPayload.map((e) => {
        return { interviewerName: '', interviewerEmail: e.interviewerEmail };
      }),
    };
    const axiosMethod = isPanel
      ? axiosUpdateInterview
      : axiosCreateInterviewPanel;

    const response = await axiosMethod(
      payload,
      (cancel: Canceler) => (makeInterviewPanelCanceler = cancel)
    );
    /* istanbul ignore else */
    if (response?.statusCode === 200) {
      setIsPanel(true);
      setInterviewerList(
        interviewsPayload.map((e) => {
          return { ...e, saved: true };
        })
      );
    } else {
      // in success scenario we need to maintain the modal, hence hide only while request failed.
      setOpenSuccessModal(false);
    }

    setApiLoader(false);
  };

  const getCurrentUserDetails = () => {
    const token = localStorage.getItem(storagePersistedKey.MSALIDToken);
    const decodedB2cToken = getDecodedB2cToken(token || '');
    setLoginUser(decodedB2cToken);
  };

  return (
    <Widget
      {...props}
      id={id}
      headingProps={{
        i18nKey: 'interviewers',
        id: 'interviewersHeader',
        className: 'kfic2-panelTitleBold',
        mb: 1,
      }}
      withDivider
    >
      {(isLoading && (
        <div>
          <Skeleton
            variant='rectangular'
            height={60}
            sx={{
              bgcolor: (theme) => theme.palette.neutral?.main,
              marginBottom: ({ typography }) => `${typography.pxToRem(10)}`,
              marginTop: ({ typography }) => `${typography.pxToRem(16)}`,
            }}
          />
          <Skeleton
            variant='rectangular'
            width={98}
            height={38}
            sx={{
              bgcolor: (theme) => theme.palette.neutral?.main,
            }}
          />
        </div>
      )) || (
        <>
          {interviewerList.length > 0 && (
            <Link
              className='kfic2-bodyTextSmallLink'
              sx={{
                position: 'absolute',
                top: ({ typography }) => `${typography.pxToRem(32)}`,
                right: ({ typography }) => `${typography.pxToRem(20)}`,
              }}
              onClick={() => {
                setOpenModal(true);
              }}
            >
              {translate('editPanel')}
            </Link>
          )}

          {interviewerList.length > 0 ? (
            <Grid
              container
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                paddingTop: ({ typography }) => `${typography.pxToRem(16)}`,
                paddingBottom: ({ typography }) => `${typography.pxToRem(16)}`,
                maxHeight: ({ typography }) => `${typography.pxToRem(140)}`,
                overflowY: 'auto',
              }}
            >
              {interviewerList?.map((e, index) => {
                return (
                  <EmployeeChip
                    key={`emp-pill-${index}-${e.interviewerId}`}
                    index={index}
                    employeeFullName={e.interviewerEmail}
                  />
                );
              })}
            </Grid>
          ) : (
            <Grid container>
              <Grid
                item
                xs={12}
                sx={{
                  paddingTop: ({ typography }) => `${typography.pxToRem(20)}`,
                }}
              >
                <Grid item xs={12}>
                  <Typography
                    className='kfic2-bodyText'
                    id='hm-panel-create-body'
                    data-testid='hm-panel-create-body'
                    sx={{ pb: ({ typography }) => `${typography.pxToRem(8)}` }}
                  >
                    {translate('interviewersPanelText')}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <KFButton
                  className='version-btn'
                  onClick={() => {
                    setOpenModal(true);
                  }}
                  data-testid='invite-interviewers'
                  color='quaternary'
                  icon={
                    <CreatePanelIcon
                      sx={{
                        mt: ({ typography }) => `${typography.pxToRem(-7)}`,
                        ml: ({ typography }) => `${typography.pxToRem(-7)}`,
                        width: ({ typography }) => `${typography.pxToRem(37)}`,
                      }}
                    />
                  }
                >
                  {translate('inviteInterviewers')}
                </KFButton>
              </Grid>
            </Grid>
          )}
        </>
      )}
      {openModal && (
        <InterviewersModal
          id='interview-modal'
          reqId={requisitionId}
          open={openModal}
          interviewers={interviewerList || []}
          jobTitle={jobTitle}
          loginUserEmail={hiringManagerEmail || ''}
          handleClose={() => {
            setOpenModal(false);
          }}
          handleSuccess={(selectedInterviewers: IInterviewers[]) => {
            onSubmitSuccess(selectedInterviewers);
          }}
        />
      )}
      {openSuccessModal && (
        <SuccessInterviewerPanelModal
          id='SuccessInterviewerPanelModal'
          reqId={requisitionId}
          open={openSuccessModal}
          showLoader={apiLoader}
          jobTitle={jobTitle}
          handleClose={() => {
            setOpenSuccessModal(false);
          }}
        />
      )}
    </Widget>
  );
};
export default Interviewers;
